import { useCallback, useEffect, useState } from "react";
import { RiDeleteBin5Line, RiExpandUpDownFill } from "react-icons/ri";
import Select from "react-select";
import toast, { Toaster } from "react-hot-toast";
import { createPortal } from "react-dom";

const Modal = ({ children }) => {
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
      {children}
    </div>,
    document.body
  );
};

const roleOptions = [
  { value: "superAdmin", label: "Super Admin" },
  { value: "admin", label: "Admin" },
  { value: "viewer", label: "Viewer" },
];

const AdminUser = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const [editAdmin, setEditAdmin] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDelete, setBulkDelete] = useState(false);

  const token = localStorage.getItem("PhilomenaAdminToken");

  const customStyles2 = {
    indicatorSeparator: () => ({
      display: 'none',
    }),
    control: (base) => ({
      ...base,
      borderColor: '#D2D0D6',
      paddingTop: '2px',
      paddingBottom: '2px',
      paddingLeft: '7px',
      boxShadow: '0 3px 6px -1px rgb(0 0 0 / 0.1)',
      borderRadius: '10px',
      borderWidth: '1px',
    }),
    menu: (base) => ({
      ...base,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#F3F0F7' : 'white',
      color: 'black',
      padding: 10,
      cursor: 'pointer',

    }),
  };

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/admin_users`,
        { headers: { Authorization: `Token ${token}` } }
      );
      const data = await res.json();
      setAdmins(data || []);
    } catch {
      toast.error("Failed to load admins");
    } finally {
      setLoading(false);
    }
  }, [token]);

  /* ================= FETCH ADMINS ================= */
  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  /* ================= HELPERS ================= */
  const getInitials = (name) => {
    if (!name) return "";

    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const toggleSelect = (id) => {
    setSelectedAdmins((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedAdmins.length === admins.length) {
      setSelectedAdmins([]);
    } else {
      setSelectedAdmins(admins.map((a) => a.id));
    }
  };

  /* ================= EDIT ADMIN ================= */
  const handleEditSave = async () => {
    const oldAdmins = [...admins];

    setAdmins((prev) =>
      prev.map((a) =>
        a.id === editAdmin.id ? { ...a, role: editAdmin.role } : a
      )
    );

    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/admin_users/${editAdmin.id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editAdmin),
        }
      );

      toast.success("Admin updated");
      setEditAdmin(null);
    } catch {
      setAdmins(oldAdmins);
      toast.error("Failed to update admin");
    }
  };

  /* ================= DELETE ================= */
  const confirmDelete = async (ids) => {
    const oldAdmins = [...admins];
    setAdmins((prev) => prev.filter((a) => !ids.includes(a.id)));

    try {
      await Promise.all(
        ids.map((id) =>
          fetch(
            `${process.env.REACT_APP_BACKEND_URL}/admin/admin_users/${id}`,
            {
              method: "DELETE",
              headers: { Authorization: `Token ${token}` },
            }
          )
        )
      );

      toast.success("Admin(s) deleted");
      setSelectedAdmins([]);
    } catch {
      setAdmins(oldAdmins);
      toast.error("Delete failed");
    } finally {
      setDeleteTarget(null);
      setBulkDelete(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="py-6 space-y-6">
      <Toaster position="top-right" />

      <h1 className="text-2xl font-medium">Admin Users</h1>

      {/* BULK ACTION BAR */}
      {selectedAdmins.length > 0 && (
        <div className="text-white p-3 rounded-lg flex justify-end items-center">
          <button
            onClick={() => setBulkDelete(true)}
            className="bg-red-600 flex items-center gap-2 px-4 py-1.5 rounded-lg"
          >
            <RiDeleteBin5Line />
            Remove User(s)
          </button>
        </div>
      )}

      {/* TABLE */}
      <div className="overflow-x-auto border rounded-xl">
        <table className="min-w-[1000px] w-full">
          <thead className="bg-[#F7F7F8] text-sm text-gray-500">
            <tr>
              <th className="px-3">
                <input
                  type="checkbox"
                  className="cursor-pointer"
                  checked={selectedAdmins.length === admins.length}
                  onChange={selectAll}
                />
              </th>
              <th className="px-3 py-3 flex gap-1 items-center">
                Admin <RiExpandUpDownFill />
              </th>
              <th className="px-3 py-3 text-start">Role</th>
              <th className="px-3 py-3 text-start">Joined</th>
              <th className="px-3 py-3 text-start">Last Login</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center py-6">
                  Loading...
                </td>
              </tr>
            ) : (
              admins.map((admin) => (
                <tr key={admin.id} className="border-t hover:bg-gray-50">
                  <td className="px-3">
                    <input
                      type="checkbox"
                      className="cursor-pointer"
                      checked={selectedAdmins.includes(admin.id)}
                      onChange={() => toggleSelect(admin.id)}
                    />
                  </td>

                  <td className="px-3 py-3 flex gap-2 items-center">
                    <div className="size-10 bg-[#62259A] rounded-full text-white flex items-center justify-center">
                      {getInitials(admin.name)}
                    </div>
                    <div>
                      <p className="font-medium">{admin.name}</p>
                      <p className="text-gray-500 text-sm">{admin.email}</p>
                    </div>
                  </td>

                  <td className="px-3 py-3">
                    <span className="px-2 py-1 rounded-lg bg-gray-100">
                      {admin.role}
                    </span>
                  </td>

                  <td className="px-3 py-3">
                    {new Date(admin.date_created).toLocaleDateString()}
                  </td>

                  <td className="px-3 py-3">
                    {admin.last_login
                      ? new Date(admin.last_login).toLocaleDateString()
                      : "—"}
                  </td>

                  <td className="px-3 py-3 text-right space-x-2">
                    <button
                      onClick={() => setEditAdmin(admin)}
                      className="border px-3 py-1 rounded-lg"
                    >
                      Edit
                    </button>

                    {admin.role !== "superAdmin" && (
                      <button
                        onClick={() => setDeleteTarget([admin.id])}
                        className="border border-red-300 text-red-600 px-3 py-1 rounded-lg"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ================= EDIT MODAL ================= */}
      {editAdmin && (
        <Modal>
          <div className="fixed inset-0 bg-[#22222259] px-2 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-[450px] sm:p-6 p-3 space-y-6 relative shadow-lg shadow-[#14151A0D] border border-[#DEE0E3]">

              {/* Header */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">Edit Admin</h2>
                  <p
                    onClick={() => setEditAdmin(null)}
                    className="bg-[#0A0F290A] cursor-pointer rounded-full size-6 flex items-center justify-center p-5"
                  >
                    ✕
                  </p>
                </div>
                <p className="text-[#0F132499] text-sm font-normal">
                  Update admin role and permissions.
                </p>
              </div>

              {/* Name (disabled) */}
              <div className="flex flex-col gap-[6px]">
                <p className="font-medium text-[#14151A]">Name</p>
                <input
                  value={editAdmin.name}
                  onChange={(e) =>
                    setEditAdmin({ ...editAdmin, name: e.value })
                  }
                  className="w-full px-[14px] py-[8px] rounded-xl border bg-[#F7F7F8] text-gray-500 shadow-md shadow-[#14151A0D] border-[#D2D0D6]"
                />
              </div>

              {/* Email (disabled) */}
              <div className="flex flex-col gap-[6px]">
                <p className="font-medium text-[#14151A]">Email address</p>
                <input
                  value={editAdmin.email}
                  onChange={(e) =>
                    setEditAdmin({ ...editAdmin, email: e.value })
                  }
                  className="w-full px-[14px] py-[8px] rounded-xl border bg-[#F7F7F8] text-gray-500 shadow-md shadow-[#14151A0D] border-[#D2D0D6]"
                />
              </div>

              {/* Role */}
              <div className="flex flex-col gap-[6px]">
                <p className="font-medium text-[#14151A]">Role</p>
                <Select
                  options={roleOptions}
                  value={roleOptions.find(
                    (r) => r.value === editAdmin.role
                  )}
                  onChange={(opt) =>
                    setEditAdmin({ ...editAdmin, role: opt.value })
                  }
                  styles={customStyles2}
                  className="w-full"
                  isSearchable={false}
                  menuPlacement="auto"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={handleEditSave}
                  className="px-4 py-2 rounded-xl bg-[#14151A] text-white hover:bg-[#333]"
                >
                  Save Changes
                </button>

                <button
                  onClick={() => setEditAdmin(null)}
                  className="px-4 py-2 rounded-xl border border-gray-200"
                >
                  Cancel
                </button>
              </div>

            </div>
          </div>
        </Modal>
      )}

      {/* ================= DELETE MODAL ================= */}
      {(deleteTarget || bulkDelete) && (
        <Modal>
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-[380px] space-y-4">
              <h2 className="text-lg font-semibold text-red-600">
                Confirm Delete
              </h2>
              <p>This action cannot be undone.</p>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setDeleteTarget(null);
                    setBulkDelete(false);
                  }}
                  className="px-4 py-2 border rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    confirmDelete(deleteTarget || selectedAdmins)
                  }
                  className="px-4 py-2 bg-red-600 text-white rounded-lg"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminUser;
